import { Button, Icon } from '@octano/global-ui';
import { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  COURSE_TYPES,
  CourseInfo,
  LegendInfo,
} from '../../../../types/CurriculumTypes';
import CourseGroupDetailModal from '../../courseGroups/parts/CourseGroupDetailModal';

export interface CurriculumSemesterProps {
  id: number;
  periodTypeName?: string | null;
  setShowCourseModal: Function;
  setCurrentSemester: Function;
  semesterCoursesList: Array<CourseInfo>;
  coursesList: Array<CourseInfo>;
  setCoursesList: Function;
  readOnly: boolean;
  hideShowCourse?: boolean;
  hideCreditsCount?: boolean;
  legend: LegendInfo;
  isCourseSelected?: (info: CourseInfo) => boolean;
  onPressCourse?: (info: CourseInfo) => void;
  setLegend: (legend: LegendInfo) => void;
  setDisabledEditButton: (value: boolean) => void;
  extraCourses?: Array<{ name: string }>;
}

const CurriculumSemester = (props: CurriculumSemesterProps) => {
  const {
    id,
    periodTypeName,
    setShowCourseModal,
    setCurrentSemester,
    semesterCoursesList,
    coursesList,
    setCoursesList,
    readOnly,
    hideShowCourse,
    hideCreditsCount,
    legend,
    onPressCourse,
    isCourseSelected,
    setLegend,
    setDisabledEditButton,
    extraCourses,
  } = props;

  const prefix = 'studyPlans.curriculum.semestersList';
  const { t } = useTranslation();

  const { mentionId, curriculumId } = useParams<{
    mentionId?: string;
    curriculumId?: string;
  }>();

  // HU 15578: detail modal for course-group cards (reuses HU 15572).
  const [openDetailGroupId, setOpenDetailGroupId] = useState<number | null>(
    null,
  );

  const getTotalCredits = () => {
    let count = 0;
    semesterCoursesList.forEach(
      (course) => (count += parseInt(course.credits.toString())),
    );
    return count;
  };

  const removeCourse = (courseToRemove: any) => {
    const auxList = [...coursesList];
    const index = coursesList.findIndex(
      (course) =>
        course.semester === courseToRemove.semester &&
        course.type === courseToRemove.type &&
        course.name === courseToRemove.name &&
        course.credits === courseToRemove.credits,
    );
    auxList.splice(index, 1);
    setCoursesList(auxList);

    let auxLegend = { ...legend };
    if (courseToRemove.isCourseGroup) {
      auxLegend = {
        ...auxLegend,
        total: auxLegend.total - courseToRemove.credits,
        required: auxLegend.required - courseToRemove.credits,
      };
    } else if (courseToRemove.type === COURSE_TYPES.OBLIGATORIO.alias) {
      auxLegend = {
        ...auxLegend,
        total: auxLegend.total - courseToRemove.credits,
        required: auxLegend.required - courseToRemove.credits,
      };
    } else if (courseToRemove.type.includes('Optativo')) {
      auxLegend = {
        ...auxLegend,
        total: auxLegend.total - courseToRemove.credits,
        optional: auxLegend.optional - courseToRemove.credits,
      };
    } else if (courseToRemove.type === COURSE_TYPES.OTROS.alias) {
      auxLegend = {
        ...auxLegend,
        total: auxLegend.total - courseToRemove.credits,
        other: auxLegend.other - courseToRemove.credits,
      };
    } else if (courseToRemove.type === COURSE_TYPES.ACTIVIDAD.alias) {
      auxLegend = {
        ...auxLegend,
        activities: auxLegend.activities - 1,
      };
    }
    // HU 15366 — descontar las horas totales de la asignatura removida.
    auxLegend = {
      ...auxLegend,
      totalHours: auxLegend.totalHours - (courseToRemove.totalHours || 0),
    };
    setLegend(auxLegend);

    if (
      (mentionId && !isNaN(parseInt(mentionId))) ||
      (curriculumId && !isNaN(parseInt(curriculumId)))
    ) {
      setDisabledEditButton(false);
    }
  };

  const handleCardClick = (course: CourseInfo) => {
    if (onPressCourse) {
      onPressCourse(course);
    }
  };

  const printCourses = () => {
    return semesterCoursesList.map((course, index) => {
      const className = `course-box ${
        course.isCourseGroup ? 'course-box--group' : ''
      } ${!!isCourseSelected && isCourseSelected(course) ? 'bg-tertiary' : ''}`
        .replace(/\s+/g, ' ')
        .trim();

      const baseStyle: CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        maxWidth: '100%',
        width: '100%',
        minHeight: '145px',
      };
      // Course-group cards rely on the SCSS class for the distinctive
      // border; regular courses keep the legacy inline border so the
      // existing visual is preserved.
      const cardStyle = course.isCourseGroup
        ? baseStyle
        : { ...baseStyle, border: '1px solid var(--secondary)' };

      return (
        <div
          key={index}
          className={className}
          style={cardStyle}
          onClick={() => handleCardClick(course)}
        >
          {course.isCourseGroup ||
          course.type === COURSE_TYPES.OBLIGATORIO.alias ||
          course.type === COURSE_TYPES.ACTIVIDAD.alias ? (
            <span className="name">{course.name}</span>
          ) : (
            <span></span>
          )}
          <div className="course-box__info">
            {course.shortening ? (
              <span>
                {t(`${prefix}.code`)}: {course.shortening}
              </span>
            ) : (
              ''
            )}
            {course.isCourseGroup ||
            course.type !== COURSE_TYPES.ACTIVIDAD.alias ? (
              <span>
                {t(`${prefix}.credits`)}: {course.credits}
              </span>
            ) : (
              ''
            )}
            <span className="type">{course.type}</span>
            {course.isCourseGroup && course.courseGroupId && (
              <span
                className="info"
                style={{
                  position: 'absolute',
                  bottom: readOnly ? '10px' : '30px',
                  right: '8px',
                  cursor: 'pointer',
                  lineHeight: 0,
                  width: 'auto',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenDetailGroupId(course.courseGroupId!);
                }}
              >
                <Icon name="information" size="16px" color="primary" />
              </span>
            )}
            {!readOnly && (
              <span
                className="delete"
                onClick={(e) => {
                  e.stopPropagation();
                  removeCourse(course);
                }}
              >
                <Icon className="trash" name="trash" key={index} size="13px" />
              </span>
            )}
          </div>
        </div>
      );
    });
  };

  const printExtraCourses = () => {
    return extraCourses?.map((course, index) => (
      <div
        key={index}
        className={`course-box`}
        style={{
          border: '1px solid var(--secondary)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          maxWidth: '100%',
          width: '100%',
          minHeight: '145px',
        }}
      >
        <span className="name">{course.name}</span>
      </div>
    ));
  };

  return (
    <div className="semester" style={{ width: '140px', marginRight: '10px' }}>
      <div className="semester-courses">
        <span className="title" style={{ width: '100%', textAlign: 'center' }}>
          {periodTypeName || t(`${prefix}.semester`)} {id}
        </span>
        {!hideShowCourse && (
          <div
            className="dashed-border"
            style={{ width: '100%', textAlign: 'center' }}
          >
            <div className="circle">
              <Button
                text=""
                type="button"
                size="sm"
                icon="plus"
                className={readOnly ? 'read-only' : ''}
                key="plus"
                onClick={() => {
                  setShowCourseModal(true);
                  setCurrentSemester(id);
                }}
                disabled={readOnly}
              />
            </div>
          </div>
        )}
        {printCourses()}
        {printExtraCourses()}
      </div>
      {!hideCreditsCount && (
        <span className="credits" style={{ width: '100%' }}>
          {t(`${prefix}.credits`)} <span>{getTotalCredits()}</span>
        </span>
      )}

      {/* HU 15578 — reuse the HU 15572 detail modal */}
      {openDetailGroupId !== null && (
        <CourseGroupDetailModal
          isOpen={true}
          groupId={openDetailGroupId}
          onClose={() => setOpenDetailGroupId(null)}
        />
      )}
    </div>
  );
};

export default CurriculumSemester;
